import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'

import { Helmet } from 'react-helmet-async'
import LoginForm from '../components/auth/LoginForm'
import { apiService } from '../services/api'

export default function LoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { isLoading, isAuthenticated, login } = useAuth()
  const [redirectTo, setRedirectTo] = useState<string>('/dashboard')
  const [isClient, setIsClient] = useState(false)
  const [loginLoading, setLoginLoading] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])

  useEffect(() => {
    if (!isClient) return
    // Get redirect destination from query params
    const redirect = searchParams.get('redirect')
    if (redirect) {
      setRedirectTo(redirect)
    }
  }, [searchParams, isClient])

  useEffect(() => {
    if (!isClient) return
    // Redirect authenticated users to dashboard or specified redirect URL
    if (isAuthenticated && !isLoading) {
      navigate(redirectTo)
    }
  }, [isAuthenticated, isLoading, navigate, redirectTo, isClient])

  // Handle login
  const handleLogin = async (email: string, password: string) => {
    setLoginLoading(true)
    try {
      const result = await login(email, password)
      // Handle password change challenge FIRST
      if (result.challengeName === 'NEW_PASSWORD_REQUIRED') {
        navigate('/change-password', { 
          state: { 
            email, 
            session: result.session,
            user: result.user 
          } 
        })
        return { data: result }
      }
      // Check if login was successful (requires BOTH tokens and user)
      if (result.tokens && result.user) {
        navigate(redirectTo)
        return { data: result }
      }
      return { 
        error: { 
          message: 'Login failed', 
          code: 'AUTH_ERROR' as const,
          retryable: true
        } 
      }
    } catch (error) {
      return { 
        error: { 
          message: error instanceof Error ? error.message : 'Login failed', 
          code: 'AUTH_ERROR' as const,
          retryable: true
        } 
      }
    } finally {
      setLoginLoading(false)
    }
  }

  // Handle send credentials
  const handleSendCredentials = async (email: string) => {
    return await apiService.sendLoginCredentials(email)
  }

  // Handle reset password
  const handleResetPassword = async (email: string) => {
    return await apiService.resetPassword(email)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <img 
            src="/fwo-logo.png" 
            alt="FlyWideOpen" 
            className="h-16 mx-auto mb-4 opacity-50 animate-pulse" 
          />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>Login - FWO Pilot Portal</title>
        <meta name="description" content="Login to the FlyWideOpen Pilot Portal" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center space-x-3">
                <img 
                  src="/fwo-logo.png" 
                  alt="FlyWideOpen" 
                  className="h-10" 
                />
                <h1 className="text-xl font-bold text-gray-900">Pilot Portal</h1>
              </Link>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Welcome Back
            </h2>
            <p className="text-lg text-gray-600">
              Access your competition portal with your registered email
            </p>
          </div>

          {/* Login Form */}
          <LoginForm
            onLogin={handleLogin}
            onSendCredentials={handleSendCredentials}
            onResetPassword={handleResetPassword}
            isLoading={loginLoading}
          />

          {/* Navigation Links */}
          <div className="mt-6 text-center space-y-2">
            <Link 
              to="/forgot-password" 
              className="text-sm text-fwo-blue hover:text-blue-700 underline block"
            >
              Forgot your password?
            </Link>
          </div>

          {/* Help Section */}
          <div className="mt-12 max-w-2xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Need Help?</h3>
              <div className="space-y-3 text-sm text-gray-600">
                <p>
                  <strong>First time logging in?</strong> Enter your email address to receive login credentials.
                </p>
                <p>
                  <strong>Can't find your email?</strong> Make sure you're using the same email address you registered with on Airtribune.
                </p>
                <p>
                  <strong>Still having trouble?</strong> Contact the competition organizers for assistance.
                </p>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-gray-200 mt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="text-center text-gray-600">
              <p>&copy; {new Date().getFullYear()} FlyWideOpen. All rights reserved.</p>
              <p className="mt-2 text-sm">
                Paragliding competition management platform
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  )
}